import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { apiRoute } from "services";
import API from "services/lib/api";
import { useSocket } from "services/socket/hook";
import {
    CLOSED_TICKET,
    NEW_TICKET_UPDATE,
    RECEIVE_MESSAGE,
    TICKET_PHASE_CHANGE,
    UPDATE_WORKSPACE_INBOX_COUNT,
} from "services/socket/events";
import { updateTicketsFilters } from "store/ticketsFilters/actions";
import { dataQueryStatus } from "utils";
import {
    getErrorMessage,
    isRequestCanceled,
    parseSocketPayload,
} from "utils/helper";
import { otherCategories, ticketPhases } from "../../enum";
import { ticketsFilterBuilder } from "./constants";
import TicketsClassificationsFilter from "./TicketsClassificationsFilter/TicketsClassificationsFilter";
import TicketsInfoFilter from "./TicketsInfoFilter/TicketsInfoFilter";
import TicketPhaseSelector from "./TicketsPhasesFilter/TicketPhaseSelector/TicketPhaseSelector";
import { ToastContext } from "components/dashboard/common/Toast/context/ToastContextProvider";
import {
    deleteIncomingTicket,
    pushIncomingTicket,
} from "store/tickets/incomingTickets/actions";
import { setActiveTicket } from "store/tickets/actions";
import MentionsFilter from "./MentionsFilter/MentionsFilter";
import { selectNextTicket } from "../ChatTickets/CustomerTicketsContainer/CustomerTickets/util/helper";
import TicketsFilterGroupActions from "./TicketsFilterGroupActions/TicketsFilterGroupActions";
import { useDoesTicketMatchFilters } from "hooks/useDoesTicketMatchFilters";
import "./TicketsMessageTagsFilter/TicketsMessageTagsFilter.scss";
import "./TicketsFilterGroup.scss";


const { ISSUE_DISCOVERY } = ticketPhases;
const { AGENT_ASSISTANCE } = otherCategories;
const COUNT_REFRESH_THROTTLE_MS = 1500;

const TicketsFilterGroup = ({
    showFilters,
    toggleFilters,
    handleRefreshData,
    refreshData,
    selectedTicketHeader,
}) => {
    const socket = useSocket();

    const dispatch = useDispatch();

    const { userId } = useSelector((state) => state.auth?.user);
    const { ticketsfilterRequest } = useSelector(
        (state) => state?.ticketsFilters,
        shallowEqual
    );

    const isTicketMatchingActiveFilters = useDoesTicketMatchFilters(); 
    const { activeTicket } = useSelector((state) => state.tickets);

    const {
        ticketPhase,
        classification,
        ticketInfoValue,
        isMentioned,
        followUpActive,
        ticketSource,
        agentId,
        search,
        keywordSearch
    } = ticketsfilterRequest || {};

    const [fetchingCount, setFetchingCount] = useState(false);
    const [countsData, setCountsData] = useState({});
    const countRequestAbortControllerRef = useRef(null);
    const throttleTimeoutRef = useRef(null);
    const ticketRefreshTimeoutRef = useRef(null);
    const countRefreshHandlerRef = useRef(null);
    const ticketUpdateHandlerRef = useRef(null);
    const lastCountRefreshRef = useRef(0);
    const pendingTrailingRefreshRef = useRef(false);
    const toastMessage = useContext(ToastContext);
    const showTicketInfoFilter = ticketPhase || isMentioned || followUpActive;
    const { sortedTickets } = useSelector((state) => state.incomingTickets);

    const handleFilterChange = (filterName, filterValue, filterOption) => {
        dispatch(
            updateTicketsFilters({
                filterName,
                filterValue,
                filterOption,
            })
        );
    };

    const getInitialTicketsCount = async (hideFetching) => {
        const requestController = new AbortController();

        try {
            !hideFetching && setFetchingCount(true);

            if (countRequestAbortControllerRef.current) {
                countRequestAbortControllerRef.current.abort();
            }
            countRequestAbortControllerRef.current = requestController;

            const res = await API.get(apiRoute?.getTicketsCount, {
                params: {
                    ...ticketsfilterRequest,
                },
                signal: requestController.signal,
            });

            if (requestController.signal.aborted || countRequestAbortControllerRef.current !== requestController) {
                return;
            }

            if (res.status === 200) {
                setCountsData(res.data.data);
                setFetchingCount(false);
            }
        } catch (err) {
            if (isRequestCanceled(err, requestController)) {
                return;
            }

            if (countRequestAbortControllerRef.current !== requestController) {
                return;
            }
            const errMssg = getErrorMessage(err);
            setFetchingCount(false);
            toastMessage(errMssg, dataQueryStatus?.ERROR);
        }
    };

    const refreshCountsThrottled = useCallback(() => {
        const now = Date.now();
        const elapsed = now - lastCountRefreshRef.current;
        const canRefreshImmediately = elapsed >= COUNT_REFRESH_THROTTLE_MS;

        const runRefresh = () => {
            pendingTrailingRefreshRef.current = false;
            lastCountRefreshRef.current = Date.now();
            countRefreshHandlerRef.current?.(true);
        };

        if (canRefreshImmediately && !pendingTrailingRefreshRef.current) {
            if (throttleTimeoutRef.current) {
                clearTimeout(throttleTimeoutRef.current);
                throttleTimeoutRef.current = null;
            }
            runRefresh();
            return;
        }

        if (pendingTrailingRefreshRef.current) {
            return;
        }

        pendingTrailingRefreshRef.current = true;
        throttleTimeoutRef.current = setTimeout(() => {
            throttleTimeoutRef.current = null;
            runRefresh();
        }, Math.max(COUNT_REFRESH_THROTTLE_MS - elapsed, 0));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshTicketsThrottled = useCallback(() => {
        if (ticketRefreshTimeoutRef.current) {
            return;
        }

        ticketRefreshTimeoutRef.current = setTimeout(() => {
            ticketRefreshTimeoutRef.current = null;
            handleRefreshData?.();
        }, 250);
    }, [handleRefreshData]);

    const isTicketOnAgentAssistancePlusIssueDiscoPhase =
        ticketPhase === ISSUE_DISCOVERY &&
        selectedTicketHeader === AGENT_ASSISTANCE;

    const handleUpdateProvision = (ticket) => {
        const parsedTicket = parseSocketPayload(ticket);

        refreshCountsThrottled();

        if (!parsedTicket?.ticketId) {
            refreshTicketsThrottled();
            return;
        }

        if (parsedTicket?.newTicketPhase) {
            if (
                activeTicket?.ticketId === parsedTicket?.ticketId &&
                isTicketOnAgentAssistancePlusIssueDiscoPhase
            ) {
                // the goal is to prevent the ticket from leaving the screen if it changes phase while being engaged with a guide
                return;
            }
        }
        if (parsedTicket?.ticketStatus === false && parsedTicket?.assigned === true && parsedTicket?.assignedTeamMemberId === userId) {
            toastMessage("Ticket has been closed");
        } else {
            if (parsedTicket?.newTicketPhase && parsedTicket?.assigned === true && parsedTicket?.assignedTeamMemberId === userId) {
                toastMessage("Ticket Moved to next phase");
            }

            if (
                parsedTicket?.commentResolved?.[0] &&
                parsedTicket?.commentResolved?.[1] === userId
            ) {
                if (activeTicket?.ticketId === parsedTicket?.ticketId) {
                    selectNextTicket(
                        activeTicket,
                        sortedTickets,
                        (ticket, ticketId) => {
                            dispatch(setActiveTicket(ticket));
                        }
                    );
                }
            }
           
            const matchFilters = isTicketMatchingActiveFilters(parsedTicket);

            if (!matchFilters) {
                dispatch(deleteIncomingTicket({ ticketId: parsedTicket.ticketId }));
                refreshTicketsThrottled();
                return;
            }
            dispatch(pushIncomingTicket({ ...parsedTicket, isNew: true }));
        }

        refreshTicketsThrottled();
    };

    const handleReceiveMessage = (message) => {
        const parsedMessage = parseSocketPayload(message);

        if (parsedMessage?.ticket?.ticketId || parsedMessage?.ticketId) {
            refreshCountsThrottled();
            refreshTicketsThrottled();
        }
    };

    countRefreshHandlerRef.current = getInitialTicketsCount;
    ticketUpdateHandlerRef.current = handleUpdateProvision;

    useEffect(() => {
        getInitialTicketsCount(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketPhase, ticketSource, refreshData, agentId, search, keywordSearch]);

    useEffect(() => {
        return () => {
            if (throttleTimeoutRef.current) {
                clearTimeout(throttleTimeoutRef.current);
            }
            if (countRequestAbortControllerRef.current) {
                countRequestAbortControllerRef.current.abort();
            }
            if (ticketRefreshTimeoutRef.current) {
                clearTimeout(ticketRefreshTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleTicketUpdate = (payload) => {
            ticketUpdateHandlerRef.current?.(payload);
        };
        const handleInboxCountUpdate = () => {
            refreshCountsThrottled();
            refreshTicketsThrottled();
        };
        const handleMessageUpdate = (payload) => {
            handleReceiveMessage(payload);
        };

        socket?.on(UPDATE_WORKSPACE_INBOX_COUNT, handleInboxCountUpdate);
        socket?.on(NEW_TICKET_UPDATE, handleTicketUpdate);
        socket?.on(TICKET_PHASE_CHANGE, handleTicketUpdate);
        socket?.on(CLOSED_TICKET, handleTicketUpdate);
        socket?.on(RECEIVE_MESSAGE, handleMessageUpdate);

        return () => {
            socket?.off(UPDATE_WORKSPACE_INBOX_COUNT, handleInboxCountUpdate);
            socket?.off(NEW_TICKET_UPDATE, handleTicketUpdate);
            socket?.off(TICKET_PHASE_CHANGE, handleTicketUpdate);
            socket?.off(CLOSED_TICKET, handleTicketUpdate);
            socket?.off(RECEIVE_MESSAGE, handleMessageUpdate);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket]);

    return (
        <div
            className={`tickets__filter_group tickets__filter ${!showFilters ? "bordered" : ""
                }`}
            id='ticketsFilterGroup'>
            <div className={`tickets__filter__container relative__layout`}>
                <div className='host__filters' key={showFilters}>
                    <div className='phase__class__container'>
                        <div className={`${showFilters ? "phase--class" : ""} `}>
                            <TicketPhaseSelector
                                ticketPhaseCounts={countsData?.ticketphase}
                                allPhaseCounts={countsData?.allticketphase}
                                selectedTicketPhase={ticketPhase}
                                fetchingCount={fetchingCount}
                                handleTicketPhaseSelect={(val) =>
                                    handleFilterChange("ticketPhase", val)
                                }
                                handleTicketSourceSelect={(val) =>
                                    handleFilterChange("ticketSource", val)
                                }
                                ticketSource={ticketSource}
                            />
                            {/* <FollowUpFilter
                                countValue={countsData?.others?.followup}
                                label={ticketsFilterBuilder?.FOLLOW_UP?.title}
                                isActive={followUpActive}
                                handleFilterChange={() =>
                                    handleFilterChange("followUpActive", true)
                                }
                                fetchingCount={fetchingCount}
                            /> */}
                            <MentionsFilter
                                countValue={countsData?.others?.isMentioned}
                                label={ticketsFilterBuilder?.MENTIONS?.title}
                                isActive={isMentioned}
                                handleFilterChange={() =>
                                    handleFilterChange("isMentioned", true)
                                }
                                fetchingCount={fetchingCount}
                            />
                            {ticketPhase ? (
                                ticketPhase !== ISSUE_DISCOVERY && (
                                    <TicketsClassificationsFilter
                                        countsData={countsData?.classifications}
                                        selectedTicketClassification={
                                            classification
                                        }
                                        fetchingCount={fetchingCount}
                                        handleTicketClassificationSelect={(
                                            val
                                        ) =>
                                            handleFilterChange(
                                                "classification",
                                                val
                                            )
                                        }
                                    />
                                )
                            ) : (
                                <></>
                            )}
                        </div>
                        {/*  */}
                        {showFilters && (
                            <TicketsFilterGroupActions
                                showFilters={showFilters}
                                toggleFilters={toggleFilters}
                                handleRefreshData={handleRefreshData}
                            />
                        )}
                    </div>
                    {showTicketInfoFilter ? (
                        <div>
                            <TicketsInfoFilter
                                filters={{
                                    ticketPhase,
                                    classification,
                                    isMentioned,
                                    followUpActive,
                                }}
                                refreshData={refreshData}
                                selectedTicketInfo={ticketInfoValue}
                                handleTicketInfoSelect={(val, filterOption) =>
                                    handleFilterChange(
                                        "ticketInfoValue",
                                        val,
                                        filterOption
                                    )
                                }
                                showingFilters={showFilters}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                    {/*  */}
                    {!showFilters && (
                        <TicketsFilterGroupActions
                            showFilters={showFilters}
                            toggleFilters={toggleFilters}
                            handleRefreshData={handleRefreshData}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default TicketsFilterGroup;
